import React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { FallbackProps } from "react-error-boundary";

export function FallbackRender({ error, resetErrorBoundary }: FallbackProps) {
  const isDevelopment = import.meta.env.DEV;

  // Type guard to ensure error is an Error object
  const errorObj = error instanceof Error ? error : new Error(String(error));

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              We're sorry, but the application encountered an unexpected error.
            </p>

            {/* Error Message */}
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <h2 className="text-sm font-semibold text-red-800 mb-2">
                Error Message:
              </h2>
              <p className="text-sm text-red-700 font-mono break-words">
                {errorObj.message || "An unknown error occurred"}
              </p>
            </div>

            {/* Stack Trace (Development Only) */}
            {isDevelopment && errorObj.stack && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900 mb-2">
                  Stack Trace (Development Only)
                </summary>
                <div className="bg-gray-100 border border-gray-300 rounded-md p-4 overflow-x-auto">
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                    {errorObj.stack}
                  </pre>
                </div>
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              {resetErrorBoundary && (
                <button
                  onClick={resetErrorBoundary}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>
              )}
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Reload Page
              </button>
            </div>

            {/* Help Text */}
            <p className="text-sm text-gray-500 mt-6">
              If this problem persists, please contact support or try clearing
              your browser cache.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
